/**
 * SiteFooter — V2 marketing footer with the internal-link mesh
 * ───────────────────────────────────────────────────────────
 * Layout from v2/landing (default) and v2/business (variant="business").
 * The guide links use keyword anchors (part of the SEO internal-linking
 * strategy), so every public page passes authority to the landing pages and
 * back. Styles: src/styles/v2.css §6 (.sf-*).
 *
 * Props (all optional)
 *   note           text after «© year CardPass» (e.g. «عرض الخدمة للشركات»), outside its LTR run
 *   variant        'default' | 'business' (v2/business copy: «كارد باس للشركات», blurb with «و»
 *                  joined to «Google Wallet»; credit under the blurb, quieter headings,
 *                  no language button in the bottom row)
 *   wrapClassName  container class (default 'sh-wrap': the same 1280px / 1360px container as
 *                  PublicHeader on every marketing page; leave it unset so header and footer line up)
 *   className      extra classes on <footer> (e.g. extra bottom space above a sticky phone CTA)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Globe02Icon } from '@hugeicons/core-free-icons';
import { toggleLocale, useLocale } from '../lib/i18n';
import { APP_STORE_URL } from '../lib/appStore.js';
import BrandLogo from './BrandLogo.jsx';
import Icon from './v2/Icon.jsx';

export default function SiteFooter({ note, variant = 'default', wrapClassName = 'sh-wrap', className = '' }) {
  const locale = useLocale();
  const ar = locale === 'ar';
  const t = (a, e) => (ar ? a : e);
  const biz = variant === 'business';
  const L = (s) => <span className="ltr">{s}</span>;

  const cols = [
    {
      key: 'product',
      h: t('المنتج', 'Product'),
      links: [
        { to: '/', l: t('الرئيسية', 'Home') },
        { to: '/pricing', l: t('الأسعار', 'Pricing') },
        { to: '/business', l: ar ? (biz ? 'كارد باس للشركات' : <>{L('CardPass')} للشركات</>) : 'CardPass for Business' },
        { to: '/employee-business-cards', l: t('بطاقات عمل الموظفين', 'Employee business cards') },
        { to: '/sales-team-business-cards', l: t('بطاقات فريق المبيعات', 'Sales team cards') },
        { to: '/business-card-printing-alternative', l: t('بديل طباعة الكروت', 'Card printing alternative') },
        { to: '/cardpass-vs-blinq', l: t('كارد باس أم Blinq؟', 'CardPass vs Blinq') },
        { to: '/cardpass-vs-hihello', l: t('كارد باس أم HiHello؟', 'CardPass vs HiHello') },
        { to: '/signup', l: t(biz ? 'أنشئ بطاقتك مجاناً' : 'أنشئ بطاقتك مجانًا', 'Create your free card') },
        { to: '/login', l: t('تسجيل الدخول', 'Log in') }
      ]
    },
    {
      key: 'guides',
      h: t('أدلة', 'Guides'),
      links: [
        { to: '/digital-business-card', l: t('بطاقة العمل الرقمية', 'Digital business card') },
        { to: '/electronic-business-card', l: t('كرت شخصي إلكتروني', 'Electronic business card') },
        { to: '/add-card-to-apple-wallet', l: t('إضافة البطاقة للمحفظة', 'Add a card to Wallet') },
        { to: '/free-business-card-maker', l: t('تصميم بطاقة عمل مجانًا', 'Free card maker') },
        { to: '/apple-wallet-business-card', l: ar ? <>بطاقة أعمال <span className="nw ltr">Apple Wallet</span></> : 'Apple Wallet business card' },
        { to: '/qr-code-business-card', l: ar ? <>بطاقة عمل بكود {L('QR')}</> : 'QR code business card' },
        { to: '/nfc-business-card', l: ar ? <>بطاقة {L('NFC')}: المقارنة</> : 'NFC card: the comparison' },
        { to: '/meeting-booking', l: t('حجز الاجتماعات', 'Meeting booking') }
      ]
    },
    {
      key: 'legal',
      h: t('قانوني', 'Legal'),
      links: [
        { to: '/privacy', l: t('سياسة الخصوصية', 'Privacy Policy') },
        { to: '/terms', l: t('شروط الاستخدام', 'Terms of Use') },
        { to: '/calendar-integration', l: t('ربط التقويم', 'Calendar connection') }
      ]
    }
  ];

  const credit = (
    <>
      {t('تصميم وتطوير المهندس محمد الزهراني', 'Designed & built by Eng. Mohammed Al-Zahrani')}
      {' · '}
      <a className="u ltr" href="https://engmohammed.net" target="_blank" rel="noopener noreferrer">engmohammed.net</a>
    </>
  );
  const year = new Date().getFullYear();
  // «© 2026 CardPass» is one LTR-isolated run on every page (approved landing note), so RTL
  // never flips it to «CardPass 2026 ©»; a note («عرض الخدمة للشركات») stays outside the run.
  const copyright = <span className="ltr">© <span className="num">{year}</span> CardPass</span>;

  return (
    <footer className={`sf${biz ? ' biz' : ''}${className ? ` ${className}` : ''}`}>
      <div className={wrapClassName}>
        <div className="sf-grid">
          <div className="sf-brand">
            <Link to="/" aria-label="CardPass" className="sf-logo">
              <BrandLogo height={biz ? 28 : 30} />
            </Link>
            <p>
              {!ar
                ? 'A digital business card that lives in Apple Wallet and Google Wallet — auto-updating, shared with a QR scan.'
                : biz
                  ? <>بطاقة عمل رقمية تعيش في {L('Apple Wallet')} و{L('Google Wallet')}، تتحدّث تلقائياً وتنشارك بمسح {L('QR')}.</>
                  : <>بطاقة عمل رقمية تعيش في <span className="nw">{L('Apple Wallet')} و {L('Google Wallet')}</span>، تتحدّث تلقائيًا وتنشارك بمسح {L('QR')}.</>}
            </p>
            {biz ? <div className="sf-by">{credit}</div> : null}
            <div>
              <a className="asb" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                aria-label={t('حمّل CardPass من App Store', 'Download CardPass on the App Store')}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
            </div>
          </div>

          {cols.map((c) => (
            <nav key={c.key} className={`sf-col ${c.key}`} aria-label={c.h}>
              <h4>{c.h}</h4>
              {c.links.map((lnk) => (
                <Link key={lnk.to} to={lnk.to}>{lnk.l}</Link>
              ))}
            </nav>
          ))}
        </div>

        <div className="sf-bot">
          {note ? <span>{copyright} · {note}</span> : copyright}
          {biz ? null : <span>{credit}</span>}
          {biz ? null : <span className="sp" />}
          {biz ? null : (
            <button type="button" className="sh-lang" onClick={toggleLocale} lang={ar ? 'en' : 'ar'}>
              <Icon icon={Globe02Icon} />{ar ? 'English' : 'العربية'}
            </button>
          )}
          <span className="ltr">cardpass.co</span>
        </div>
      </div>
    </footer>
  );
}
